/**
 * CapacitorLayout — for the native iOS/Android app shell.
 *
 * Key differences from browser layouts:
 *  - No URL bar / navigation bar to account for (we own the whole screen)
 *  - Status bar area handled via env(safe-area-inset-top) — usually 44-59px on notch iPhones
 *  - Home indicator: env(safe-area-inset-bottom) — 34px on iPhone X+, 0 on older/Android
 *  - Can use Capacitor StatusBar + Keyboard plugins if installed
 *  - Tab bar uses gesture-safe padding at the very bottom
 *
 * Performance: same rules as MobileLayout — no backdrop-filter, no blur,
 * GPU-promoted fixed elements via translateZ(0), passive listeners.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Calendar, MessageSquare, Bell,
  Users, BarChart2, Settings, LogOut, Shield, X, ChevronLeft, ChevronRight, ChevronDown, Layers, DollarSign, Receipt, Menu, Trophy, Radio, Globe,
} from 'lucide-react';
import { PRESS_BOX, pressBoxVisible, pressBoxNavItems } from '../constants/pressBox.js';
import { TEAM_SETTINGS_SUBSECTIONS } from './teamSettingsSubNav.js';
import { pathForTeamSwitch } from './teamSwitchNav.js';
import { useAuth } from '../context/AuthContext.jsx';
import { usePWAContext } from '../context/PWAContext.jsx';
import { chatSocket } from '../services/socket.js';
import api from '../services/api.js';
// v2.5.124 — Phase 11 League Module.
import { useLeagueModeNav } from '../hooks/useLeagueModeNav.js';
import { useCurrentSeason } from '../hooks/useCurrentSeason.js';
import { useCapacitorPlugins, haptics, statusBar } from '../hooks/useCapacitorPlugins.js';
import OnboardingFlow from '../components/OnboardingFlow.jsx';
import VersionTag from '../components/shared/VersionTag.jsx';
import { iconForNotifType } from '../utils/notifIcons.js';
import { pickActiveTeam, saveLastTeamId } from '../utils/lastTeam.js';

// Theme-aware colour tokens — read from CSS custom properties
// so all three themes (midnight/arena/zen) work correctly in native context.
const S = {
  bg:      'var(--bg)',
  surface: 'var(--surface)',
  tabBar:  'var(--bottombar-bg)',
  topBar:  'var(--topbar-bg)',
  border:  'var(--border)',
  text:    'var(--text)',
  text2:   'var(--text-secondary)',
};

const touchBtn = {
  WebkitTapHighlightColor: 'transparent',
  touchAction: 'manipulation',
  userSelect: 'none',
  WebkitUserSelect: 'none',
  cursor: 'pointer',
  border: 'none',
  background: 'transparent',
};

// ─── Full-screen drawer ───────────────────────────────────────────
function CapacitorDrawer({ open, onClose, teams, activeTeam, setActiveTeam, user, onLogout, currentSeasonId, currentSeasonName }) {
  const location  = useLocation();
  const navigate  = useNavigate();
  const teamColor = activeTeam?.settings?.team_color || '#00d4ff';
  const prevPath  = useRef(location.pathname);

  // Close the drawer only when the ROUTE changes while it is open.
  // The old one-liner `useEffect(() => { if (open) onClose(); }, [..., open, ...])`
  // was wrong: it fired whenever `open` changed to true, so the drawer
  // called onClose() immediately on every open attempt.
  useEffect(() => {
    if (open && location.pathname !== prevPath.current) {
      prevPath.current = location.pathname;
      onClose();
    } else {
      prevPath.current = location.pathname;
    }
  }, [location.pathname, open, onClose]);

  // Apr 2026 — split nav by role.
  // Players see only Dashboard, Schedule, Chat, Team. Players page,
  // Seasons, and Stats are coach/manager-only.
  const isCoachish = activeTeam && ['coach','manager'].includes(activeTeam.member_role);

  // 2026-10-18 — a Press Box team gets its own slim menu (games, roster,
  // site & settings) instead of the full team nav.
  const navItems = activeTeam?.team_type === 'press_box'
    ? pressBoxNavItems(activeTeam, { Radio, Users, Settings, BarChart3: BarChart2, Globe, Trophy })
    : activeTeam ? [
    { to: '/dashboard',                      icon: LayoutDashboard, label: 'Dashboard',   end: true },
    { to: `/team/${activeTeam.id}/games`,    icon: Calendar,        label: 'Schedule' },
    // Apr 30 2026 — Scoreboards. Visible to all roster members,
    // hidden when team setting scoreboard_enabled === false.
    ...(activeTeam.settings?.scoreboard_enabled !== false ? [
      { to: `/team/${activeTeam.id}/scoreboards`, icon: Trophy, label: 'Scoreboards' },
    ] : []),
    ...(isCoachish ? [
      { to: `/team/${activeTeam.id}/players`,  icon: Users,           label: 'Players' },
    ] : []),
    { to: `/team/${activeTeam.id}/chat`,     icon: MessageSquare,   label: 'Chat' },
    ...(isCoachish ? [
      { to: `/team/${activeTeam.id}/seasons`,  icon: Layers,          label: 'Seasons' },
      { to: `/team/${activeTeam.id}/stats`,    icon: BarChart2,       label: 'Stats' },
      // Apr 2026 (Tom_Notes E2) — Finance tab. Coach-only.
      // Apr 2026 mobile-parity — was missing on Capacitor; coaches
      // using the iOS/Android wrapped app couldn't reach finance
      // without going to the desktop. Added here to match Mobile/
      // Desktop/Tablet sidebar inventories exactly.
      { to: `/team/${activeTeam.id}/finance`,  icon: DollarSign,      label: 'Finance' },
      // Budget is OFF by default — only shown when a super admin has
      // enabled it for this team (teams.settings.budget_enabled).
      ...(activeTeam.settings?.budget_enabled === true ? [
        { to: `/team/${activeTeam.id}/budget`,  icon: DollarSign,      label: 'Budget' },
      ] : []),
      // Apr 2026 mobile-parity — coach-only notification dispatch
      // history. Was already on MobileLayout but missing here. The
      // backend 403s players, so hidden for non-coaches to avoid a
      // dead-end nav link.
      { to: `/team/${activeTeam.id}/notification-log`, icon: Bell,    label: 'Notification Log' },
    ] : [
      // May 4 2026 (v2.5.70) — player-scoped Stats. See DesktopLayout
      // for full rationale.
      { to: `/team/${activeTeam.id}/my-stats`, icon: BarChart2,       label: 'Stats' },
    ]),
    // May 4 2026 (v2.5.70) — Team Settings entry hidden entirely for
    // non-coach players. See MobileLayout for rationale.
    ...(isCoachish ? [
      { to: `/team/${activeTeam.id}/settings`,  icon: Settings,        label: 'Team',
        // Apr 2026 — expandable sub-nav (coach/manager only). Same
        // pattern as MobileLayout: drawer auto-closes after route
        // change, but reopens already expanded for the active section
        // since expansion is derived from the URL.
        children: TEAM_SETTINGS_SUBSECTIONS.map(s => ({
          to: `/team/${activeTeam.id}/settings/${s.id}`,
          label: s.label,
          end: true,
        })),
      },
    ] : []),
    // v2.5.118 — Leagues nav is gated by the per-user 'leagues'
    // feature flag (see MobileLayout for rationale).
  ] : [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', end: true },
  ];

  if (user?.featureFlags?.leagues) {
    navItems.push({ to: '/leagues', icon: Trophy, label: 'Leagues', accent: 'gold' });
  }
  // 2026-10-17 — Press Box (single-team stats), gated like Leagues: the
  // pressBox flag, or the super admin. The server re-checks every call.
  if (pressBoxVisible(user) && activeTeam?.team_type !== 'press_box') {
    navItems.push({ to: '/press-box', icon: Radio, label: PRESS_BOX.NAME, accent: 'gold' });
  }

  // v2.5.124 — see DesktopLayout for rationale.
  const { inLeagueMode, leagueNavItems, league } = useLeagueModeNav();

  // 2026-10-22 (per Gilles) — an account whose teams are ALL Press Box sees
  // ONLY the Press Box menu, on every surface (web, desktop, mobile app).
  const pbOnlyTeams = (teams || []).filter(t => t.team_type === 'press_box');
  const pressBoxOnlyAccount = pbOnlyTeams.length > 0 && pbOnlyTeams.length === (teams || []).length;
  const navItemsPb = pressBoxOnlyAccount && activeTeam?.team_type !== 'press_box'
    ? pressBoxNavItems(pbOnlyTeams[0], { Radio, Users, Settings, BarChart3: BarChart2, Globe, Trophy })
    : navItems;
  const effectiveNavItems = inLeagueMode && leagueNavItems ? leagueNavItems : navItemsPb;

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, zIndex: 490,
        background: 'rgba(0,0,0,0.7)',
        opacity: open ? 1 : 0,
        pointerEvents: open ? 'auto' : 'none',
        transition: 'opacity 0.2s ease',
      }} />

      <div style={{
        position: 'fixed', top: 0, left: 0, bottom: 0,
        width: 290,
        zIndex: 500,
        background: `linear-gradient(160deg, ${S.surface} 0%, ${S.bg} 100%)`,
        borderRight: `1px solid ${S.border}`,
        display: 'flex', flexDirection: 'column',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
        transform: open ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.26s cubic-bezier(0.32,0.72,0,1)',
        willChange: 'transform',
        // Native safe areas
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
      }}>
        {/* Status bar spacer + header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 16px 14px',
          borderBottom: `1px solid ${S.border}`, flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, background: `${teamColor}18`,
              border: `1px solid ${teamColor}30`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
            }}>🏟</div>
            <div>
              <div style={{
                fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 20,
                letterSpacing: '0.08em', textTransform: 'uppercase', color: S.text,
              }}>Game Day</div>
              <div style={{
                fontFamily: 'var(--font-display)', fontSize: 8, letterSpacing: '0.2em',
                textTransform: 'uppercase', color: teamColor,
              }}>TEAM MANAGER</div>
            </div>
          </div>
          <button onClick={onClose} style={{ ...touchBtn, padding: 10, borderRadius: 8, minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={18} color={S.text2} />
          </button>
        </div>

        {/* League-mode header — v2.5.124. */}
        {inLeagueMode && (
          <div style={{ padding: '12px 12px 6px', flexShrink: 0 }}>
            <p style={{
              fontFamily: 'var(--font-display)', fontSize: 9, letterSpacing: '0.18em',
              textTransform: 'uppercase', color: S.text2, padding: '0 6px', marginBottom: 8,
            }}>LEAGUE MODE</p>
            <div style={{
              padding: '11px 12px', borderRadius: 10, marginBottom: 4,
              background: 'rgba(178, 132, 255, 0.10)',
              border: '1px solid rgba(178, 132, 255, 0.30)',
              display: 'flex', alignItems: 'center', gap: 10, minWidth: 0,
            }}>
              <Trophy size={14} style={{ color: '#b284ff', flexShrink: 0 }} />
              <span style={{
                fontSize: 14, fontWeight: 700, color: S.text,
                overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                flex: 1, minWidth: 0,
              }}>{league?.name || 'Loading…'}</span>
            </div>
            <button
              onClick={() => { haptics.impact('medium'); navigate('/dashboard'); onClose(); }}
              style={{
                ...touchBtn, width: '100%', textAlign: 'left',
                padding: '11px 12px', borderRadius: 10, marginTop: 4, minHeight: 44,
                background: 'transparent', border: `1px dashed ${S.border}`,
                display: 'flex', alignItems: 'center', gap: 10,
                color: S.text2, fontFamily: 'var(--font-body)', fontSize: 14,
              }}>
              <ChevronLeft size={14} style={{ flexShrink: 0 }} />
              <span style={{ flex: 1, fontWeight: 600 }}>Back to teams</span>
            </button>
          </div>
        )}

        {/* Teams */}
        {!inLeagueMode && teams.length > 0 && (
          <div style={{ padding: '12px 12px 6px', flexShrink: 0 }}>
            <p style={{
              fontFamily: 'var(--font-display)', fontSize: 9, letterSpacing: '0.18em',
              textTransform: 'uppercase', color: S.text2, padding: '0 6px', marginBottom: 8,
            }}>YOUR TEAMS</p>
            {teams.map(t => {
              const c = t.settings?.team_color || '#00d4ff';
              const isA = activeTeam?.id === t.id;
              return (
                <button key={t.id} onClick={() => {
                  haptics.impact('medium');
                  // Apr 30 2026 — same fix as DesktopLayout: team
                  // switch needs to also rewrite the URL so the
                  // route-driven page picks up the new team id.
                  setActiveTeam(t);
                  if (activeTeam?.id !== t.id) {
                    const target = pathForTeamSwitch(location.pathname, activeTeam?.id, t.id, t);
                    if (target) navigate(target);
                  }
                  onClose();
                }} style={{
                  ...touchBtn, width: '100%', textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '11px 12px', borderRadius: 10, marginBottom: 3,
                  minHeight: 50,
                  background: isA ? `${c}12` : 'transparent',
                  border: `1px solid ${isA ? `${c}28` : 'transparent'}`,
                }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: c, opacity: isA ? 1 : 0.3, flexShrink: 0 }} />
                  <span style={{
                    flex: 1, fontSize: 15, fontWeight: isA ? 600 : 400,
                    color: isA ? c : S.text2, fontFamily: 'var(--font-body)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{t.name}</span>
                  {isA && <ChevronRight size={14} color={c} />}
                </button>
              );
            })}
          </div>
        )}

        <div style={{ height: 1, background: S.border, margin: '4px 16px 8px' }} />

        {/* Nav */}
        <nav style={{ flex: 1, padding: '4px 12px' }}>
          {activeTeam && (
            <button onClick={() => { navigate(`/team/${activeTeam.id}/seasons`); onClose?.(); }}
              title="The season every page is currently showing. Tap to switch."
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', margin: '0 0 8px', padding: '9px 12px', borderRadius: 10,
                background: currentSeasonId ? 'var(--ice-a8, rgba(0,212,255,0.08))' : 'var(--surface-2)',
                border: `1px solid ${currentSeasonId ? 'var(--ice-a30, rgba(0,212,255,0.30))' : 'var(--border)'}`, cursor: 'pointer', textAlign: 'left' }}>
              <Layers size={15} style={{ color: currentSeasonId ? 'var(--ice)' : 'var(--text-3)', flexShrink: 0 }} />
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: 'block', fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)', lineHeight: 1.2 }}>Season</span>
                <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: currentSeasonId ? 'var(--ice)' : 'var(--text-2)', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                  {currentSeasonId ? (currentSeasonName || 'Selected season') : 'All seasons'}
                </span>
              </span>
            </button>
          )}
          {effectiveNavItems.map(item => {
            const hasChildren = Array.isArray(item.children) && item.children.length > 0;
            const expanded    = hasChildren && location.pathname.startsWith(item.to);
            // v2.5.126 — gold accent for Leagues nav item.
            const itemAccentColor = item.accent === 'gold' ? 'var(--gold)' : teamColor;
            const itemAccentBg    = item.accent === 'gold' ? 'var(--gold-a15)' : `${teamColor}12`;
            const itemAccentBd    = item.accent === 'gold' ? 'var(--gold-a30)' : `${teamColor}28`;
            const itemIdleColor   = item.accent === 'gold' ? 'var(--gold-a50)' : S.text2;
            return (
              <div key={item.to}>
                <NavLink to={item.to} end={item.end} style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '13px 14px', borderRadius: 10, marginBottom: 2,
                  textDecoration: 'none', minHeight: 50,
                  background: isActive ? itemAccentBg : 'transparent',
                  border: `1px solid ${isActive ? itemAccentBd : 'transparent'}`,
                  color: isActive ? itemAccentColor : itemIdleColor,
                  fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: isActive ? 600 : 400,
                })}>
                  <item.icon size={19} />
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {hasChildren && (
                    <ChevronDown size={17} style={{
                      flexShrink: 0,
                      transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                      transition: 'transform 0.18s',
                      opacity: 0.6,
                    }} />
                  )}
                </NavLink>
                {expanded && (
                  <div style={{ marginLeft: 14, marginBottom: 6, borderLeft: `1.5px solid ${S.border}`, paddingLeft: 6 }}>
                    {item.children.map(child => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        end={child.end}
                        style={({ isActive: childActive }) => ({
                          display: 'block',
                          padding: '11px 14px',
                          marginBottom: 2,
                          borderRadius: 8,
                          textDecoration: 'none',
                          fontFamily: 'var(--font-body)',
                          fontSize: 15,
                          fontWeight: childActive ? 600 : 400,
                          color: childActive ? teamColor : S.text2,
                          background: childActive ? `${teamColor}10` : 'transparent',
                          minHeight: 42,
                        })}>
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div style={{ padding: '8px 12px 16px', flexShrink: 0, borderTop: `1px solid ${S.border}` }}>
          <NavLink to="/profile" style={({ isActive }) => ({
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '12px 14px', borderRadius: 10, marginBottom: 4,
            textDecoration: 'none', minHeight: 50,
            background: isActive ? 'var(--ice-a8)' : 'transparent',
            color: isActive ? 'var(--ice)' : S.text2,
            fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 500,
          })}>
            <div style={{
              width: 32, height: 32, borderRadius: 9, flexShrink: 0,
              background: 'var(--ice-a12)', border: '1px solid var(--ice-border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, color: 'var(--ice)',
            }}>
              {user?.name?.[0]?.toUpperCase() || '?'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: S.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</div>
              <div style={{ fontSize: 9, color: '#00e87a', fontFamily: 'var(--font-display)', letterSpacing: '0.08em' }}>● ONLINE</div>
            </div>
          </NavLink>
          {/* Apr 2026 mobile-parity — Activity link, conditional on
              transaction history. Only shows when the user has paid
              for spares/seasons. Was missing from CapacitorLayout
              while present on Desktop/Tablet/Mobile. */}
          {user?.hasPaymentActivity && (
            <NavLink to="/activity" style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '12px 14px', borderRadius: 10, marginBottom: 3,
              textDecoration: 'none', minHeight: 50,
              background: isActive ? 'var(--ice-a8)' : 'transparent',
              color: isActive ? 'var(--ice)' : S.text2,
              fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 500,
            })}>
              <Receipt size={17} /> Activity
            </NavLink>
          )}
          <button onClick={onLogout} style={{
            ...touchBtn, width: '100%', textAlign: 'left',
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '12px 14px', borderRadius: 10, minHeight: 50,
            color: 'rgba(255,90,90,0.6)', fontFamily: 'var(--font-body)', fontSize: 15,
          }}>
            <LogOut size={17} />
            Sign out
          </button>
          {user?.role === 'admin' && (
            <NavLink to="/admin" style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '12px 14px', borderRadius: 10, textDecoration: 'none', minHeight: 50,
              color: isActive ? '#ff6040' : 'rgba(255,96,64,0.5)', fontFamily: 'var(--font-body)', fontSize: 15,
            })}>
              <Shield size={17} />
              Admin
            </NavLink>
          )}
          {/* Apr 30 2026 — version tag at bottom of drawer. Tappable
              for build details (frontend + backend semver + SHA +
              drift warning if mismatched). Same shared component as
              all other layouts. */}
          <div style={{ paddingTop: 4, marginTop: 4, borderTop: `1px solid ${S.border}` }}>
            <VersionTag />
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Native tab bar ────────────────────────────────────────────────
// Apr 2026 — redesigned to a 4-tab layout (Home | Schedule | Chat | More)
// per user request. Matches the BottomNav component used in the PWA
// MobileLayout. The "More" tab opens the side drawer where Alerts,
// Profile, Players, Seasons, etc. all live. This keeps the tab bar
// uncluttered and matches the consumer-app pattern users expect from
// native iOS/Android apps. The "More" tab shows the unread-notification
// count as a badge so users still see at-a-glance that something is
// pending behind the menu.
function NativeTabBar({ activeTeam, unreadCount, unreadNotifs, onMoreClick, teamColor }) {
  const location = useLocation();
  const color = teamColor || '#00d4ff';

  // For the Schedule and Chat tabs, when there's no active team yet,
  // fall back to /dashboard so an accidental tap doesn't 404. Tabs
  // visibly dim when disabled but the bar's geometry stays stable.
  const fallback = '/dashboard';

  const tabs = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Home', end: true },
    {
      to: activeTeam ? `/team/${activeTeam.id}/games` : fallback,
      icon: Calendar, label: 'Schedule',
      disabled: !activeTeam,
    },
    {
      to: activeTeam ? `/team/${activeTeam.id}/chat` : fallback,
      icon: MessageSquare, label: 'Chat', badge: unreadCount,
      disabled: !activeTeam,
    },
    { onClick: onMoreClick, icon: Menu, label: 'More', badge: unreadNotifs },
  ];

  return (
    <nav
      data-tab-bar
      style={{
        position: 'fixed',
        bottom: 0, left: 0, right: 0,
        zIndex: 400,
        background: S.tabBar,
        // Top border with ice glow
        borderTop: `1px solid ${S.border}`,
        // Native home indicator padding
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        display: 'flex', alignItems: 'stretch',
        transform: 'translateZ(0)',
        contain: 'layout style paint',
      }}>
      {tabs.map((tab, i) => {
        const isActive = tab.to && !tab.disabled && (
          tab.end ? location.pathname === tab.to : location.pathname.startsWith(tab.to)
        );
        // Apr 2026 — disabled tabs (Schedule/Chat when no team yet)
        // dim further to telegraph "not available" without removing
        // them from the geometry.
        const tabColor = tab.disabled ? 'var(--tab-inactive)' : (isActive ? color : 'var(--tab-inactive)');
        const tabOpacity = tab.disabled ? 0.4 : 1;

        const inner = (
          <span style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: 4, padding: '11px 0 9px', position: 'relative', width: '100%',
            opacity: tabOpacity,
          }}>
            {isActive && (
              <span style={{
                position: 'absolute', top: 6, width: 20, height: 2, borderRadius: 1,
                background: color, boxShadow: `0 0 6px ${color}`,
              }} />
            )}
            <span style={{ position: 'relative' }}>
              <tab.icon size={24} color={tabColor} strokeWidth={isActive ? 2.2 : 1.7} />
              {(tab.badge || 0) > 0 && (
                <span style={{
                  position: 'absolute', top: -5, right: -7,
                  minWidth: 16, height: 16, padding: '0 3px',
                  borderRadius: 8, background: '#ff3b3b',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 9, fontWeight: 800, color: '#fff', lineHeight: 1,
                }}>
                  {tab.badge > 9 ? '9+' : tab.badge}
                </span>
              )}
            </span>
            <span style={{
              fontSize: 11, fontWeight: isActive ? 700 : 500,
              fontFamily: 'var(--font-display)',
              letterSpacing: isActive ? '0.07em' : '0.03em',
              textTransform: 'uppercase', color: tabColor,
              // Apr 2026 — nowrap + ellipsis. Even on the narrowest
              // phones (320px iPhone SE 1) where each tab gets 80px,
              // labels stay on one line and the bar height stays
              // constant. If user scales fonts up via system
              // accessibility, the label truncates rather than
              // wrapping into a second line which would push content
              // under the bar.
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
            }}>{tab.label}</span>
          </span>
        );

        const shared = {
          key: i,
          style: {
            ...touchBtn, flex: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            // Apr 2026 — fully suppress disabled-tab interaction so
            // accidental edge taps don't navigate to /dashboard
            // (which would otherwise still happen from the fallback).
            pointerEvents: tab.disabled ? 'none' : 'auto',
          },
          // Opacity feedback: dim on touch, restore on release or cancel
          onTouchStart(e) { if (!tab.disabled) e.currentTarget.style.opacity = '0.65'; },
          onTouchEnd(e)   { e.currentTarget.style.opacity = '1'; },
          onTouchCancel(e){ e.currentTarget.style.opacity = '1'; },
          // Haptics in onClick, not onTouchStart — onClick only fires for confirmed taps,
          // not for scroll gestures where touchstart fires then touchcancel fires.
          // onTouchStart-based haptics would buzz on every swipe attempt in the tab bar area.
        };

        if (tab.onClick) return (
          <button {...shared} onClick={() => { haptics.impact('light'); tab.onClick(); }}>{inner}</button>
        );
        return (
          <NavLink {...shared} to={tab.to} end={tab.end} onClick={() => haptics.impact('light')}>{inner}</NavLink>
        );
      })}
    </nav>
  );
}

// ─── CapacitorLayout ──────────────────────────────────────────────
export default function CapacitorLayout({ isIOS, isAndroid }) {
  const { user, logout } = useAuth();
  const { offline } = usePWAContext();
  const navigate = useNavigate();
  const location = useLocation();

  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [showNotifs, setShowNotifs]   = useState(false);
  const [teams, setTeams]             = useState([]);
  // May 1 2026 — same teamsLoaded sentinel as DesktopLayout. See
  // that file for full rationale; keeps DashboardPage's empty state
  // from flashing during the gap between mount and /teams resolving.
  const [teamsLoaded, setTeamsLoaded] = useState(false);
  const [activeTeam, setActiveTeam]   = useState(null);
  const { currentSeasonId, currentSeasonName, setCurrentSeason, seasonLoaded } = useCurrentSeason(activeTeam?.id || null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);

  const teamColor = activeTeam?.settings?.team_color || '#00d4ff';

  // ── Capacitor native plugins ─────────────────────────────────────
  useCapacitorPlugins({
    onBackButton({ canGoBack }) {
      if (drawerOpen) { setDrawerOpen(false); return; }
      if (showNotifs)  { setShowNotifs(false); return; }
      if (canGoBack)   { window.history.back(); }
      // else: minimise (handled inside useCapacitorPlugins)
    },
  });

  const loadTeams = useCallback(async (lastTeamId = null, _attempt = 0) => {
    try {
      const { data } = await api.get('/teams');
      setTeams(data);
      // Apr 2026 — pickActiveTeam uses the saved last-team id we
      // pass in (avoids stale-closure bug — see DesktopLayout for
      // the full rationale). Native iOS/Android wrappers especially
      // benefit from this since the JS context dies on backgrounding
      // and the user's preference would otherwise be lost between
      // sessions.
      setActiveTeam(prev => pickActiveTeam(data, prev, lastTeamId));
      setTeamsLoaded(true);
    } catch (err) {
      // v2.5.184 — retry transient failures instead of flashing the
      // "not on a schedule" empty state. See DesktopLayout.
      console.error('Failed to load teams:', err);
      const status = err?.response?.status;
      if (status === 401) { setTeamsLoaded(true); return; }
      if (_attempt < 1) { setTimeout(() => loadTeams(lastTeamId, _attempt + 1), 1500); return; }
      setTeamsLoaded(true);
    }
  }, []);

  // Apr 2026 — persist active team to users.last_active_team_id.
  useEffect(() => {
    if (activeTeam?.id) saveLastTeamId(activeTeam.id);
  }, [activeTeam?.id]);

  const loadNotifications = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications');
      setNotifications(data);
    } catch {}
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty('--ice', teamColor);
    // Sync Android status bar background colour to team colour (tinted dark version).
    // On iOS this is ignored — iOS StatusBar only supports Dark/Light style, not colour.
    statusBar.setColor(S.bg);  // keep it dark; team colour is shown via the accent bar
  }, [teamColor]);

  useEffect(() => {
    if (user?.id) {
      loadTeams(user.lastActiveTeamId || null);
    }
    const token = localStorage.getItem('token');
    if (token) chatSocket.connect(token);
    return () => chatSocket.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // 2026-07-21 (multi-team email-link landing audit) — correct activeTeam
  // when it drifts from the team the CURRENT URL is actually pointing at.
  // See DesktopLayout.jsx for the full rationale — same fix, same shape,
  // applied to every layout since each keeps its own activeTeam state.
  // Matters especially here: native apps keep the layout mounted across
  // background/foreground cycles, so a push-notification or universal-
  // link deep-link (initDeepLinks in main.jsx) navigates via SPA routing
  // with NO remount — a stale localStorage pin alone would never be
  // re-read; this effect is what actually corrects the live session.
  useEffect(() => {
    if (!teamsLoaded || teams.length === 0) return;
    const match = location.pathname.match(/^\/team\/([^/]+)/);
    const urlTeamId = match ? match[1] : null;
    if (!urlTeamId || urlTeamId === activeTeam?.id) return;
    const urlTeam = teams.find(t => t.id === urlTeamId);
    if (!urlTeam) return; // not a member (or a bad id) — leave activeTeam as-is
    setActiveTeam(urlTeam);
    try { localStorage.setItem('gd:lastTeamId', urlTeamId); } catch { /* storage unavailable — non-fatal */ }
  }, [location.pathname, teams, teamsLoaded, activeTeam?.id]);

  useEffect(() => { loadNotifications(); }, []);

  useEffect(() => {
    const id = setInterval(loadNotifications, 60_000); // 1-min polling in native app
    return () => clearInterval(id);
  }, [loadNotifications]);

  useEffect(() => {
    const unsub = chatSocket.on('NEW_MESSAGE', () => {
      if (!location.pathname.includes('/chat')) setUnreadCount(c => c + 1);
    });
    return unsub;
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname.includes('/chat')) setUnreadCount(0);
  }, [location.pathname]);

  useEffect(() => {
    const unsub = chatSocket.on('NEW_NOTIFICATION', notif => {
      if (notif) setNotifications(prev => [{ ...notif, read_at: null }, ...prev.filter(n => n.id !== notif.id)]);
      else loadNotifications();
    });
    return unsub;
  }, [loadNotifications]);

  const handleLogout      = () => { logout(); navigate('/login'); };
  const handleDrawerClose = useCallback(() => setDrawerOpen(false), []);
  const unreadNotifs = notifications.filter(n => !n.read_at).length;

  // Tab bar + home indicator height
  const TABBAR_H = 58;

  // When the keyboard opens (set by useCapacitorPlugins keyboard listener),
  // shrink the bottom padding so content scrolls up above the keyboard.
  // .keyboard-open class and --keyboard-h var are set in useCapacitorPlugins.

  // Apr 30 2026 (App_Change_List #2) — cycle prev/next through the
  // user's teams. Mirrors MobileLayout's helper. Wraps at the ends.
  // URL rewrite via pathForTeamSwitch keeps the user on the same
  // page (e.g. /players → /players for the new team).
  const cycleToTeam = useCallback((direction) => {
    if (!Array.isArray(teams) || teams.length <= 1) return;
    const idx = teams.findIndex(t => t.id === activeTeam?.id);
    if (idx === -1) return;
    const nextIdx = direction === 'next'
      ? (idx + 1) % teams.length
      : (idx - 1 + teams.length) % teams.length;
    const nextTeam = teams[nextIdx];
    if (!nextTeam || nextTeam.id === activeTeam?.id) return;
    haptics.impact('light');
    setActiveTeam(nextTeam);
    const target = pathForTeamSwitch(location.pathname, activeTeam?.id, nextTeam.id, nextTeam);
    if (target) navigate(target);
  }, [teams, activeTeam, location.pathname, navigate]);

  return (
    // 100vh is correct for Capacitor — the webview owns the entire screen with no
    // dynamic browser toolbar. dvh would also work but isn't supported on WebView < 108
    // and our minWebViewVersion is 80, so we stay with plain vh here.
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: S.bg, overflow: 'hidden' }}>

      {/* Status bar spacer (notch iPhones, Android status bar) */}
      <div style={{
        height: 'env(safe-area-inset-top, 0px)',
        background: S.topBar,
        flexShrink: 0,
        transform: 'translateZ(0)',
      }} />

      {/* ── Offline strip ─────────────────────────────────────── */}
      {offline.isOffline && (
        <div style={{
          background: 'rgba(255,59,59,0.95)', flexShrink: 0,
          padding: '7px 16px', textAlign: 'center',
          fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 700,
          letterSpacing: '0.12em', textTransform: 'uppercase', color: '#fff',
        }}>
          ● Offline — cached data
        </div>
      )}

      {/* ── Top bar ───────────────────────────────────────────── */}
      <div style={{
        background: S.topBar,
        borderBottom: `1px solid ${S.border}`,
        height: 52, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 12px',
        contain: 'layout style paint',
        transform: 'translateZ(0)',
        zIndex: 300,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
          <button
            onClick={() => { haptics.impact('light'); setDrawerOpen(true); }}
            style={{ ...touchBtn, padding: 8, borderRadius: 8, minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
          >
            {/* Hamburger — custom icon (larger for native feel) */}
            <svg width="22" height="16" viewBox="0 0 22 16" fill="none">
              <rect width="22" height="2.5" rx="1.25" fill={S.text2} />
              <rect y="6.75" width="16" height="2.5" rx="1.25" fill={S.text2} />
              <rect y="13.5" width="22" height="2.5" rx="1.25" fill={S.text2} />
            </svg>
          </button>
          <span style={{ fontSize: 22, lineHeight: 1, flexShrink: 0 }}>🏟</span>
          {/* Apr 30 2026 (App_Change_List #2) — "Game Day" wordmark
              dropped on Capacitor mobile, matching MobileLayout. The
              team name is the brand identity inside a team's context;
              the wordmark was eating space the team name needed. */}
          {activeTeam && teams.length > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 2,
              flex: 1, minWidth: 0,
            }}>
              {/* Apr 30 2026 (App_Change_List #2) — explicit prev/next
                  cycle buttons. Hidden when only one team. Replaces
                  the old "tap-team-name-to-cycle-forward" pattern with
                  obvious affordances; the team-name button itself
                  still cycles forward for back-compat. */}
              {teams.length > 1 && (
                <button
                  onClick={() => cycleToTeam('prev')}
                  aria-label="Previous team"
                  style={{
                    ...touchBtn,
                    padding: 6, borderRadius: 6,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    minWidth: 32, minHeight: 32, flexShrink: 0,
                  }}>
                  <ChevronLeft size={18} color={teamColor || 'var(--ice)'} strokeWidth={2} />
                </button>
              )}
              <button
                onClick={() => cycleToTeam('next')}
                title={teams.length > 1 ? `Switch team (${teams.length} teams)` : activeTeam.name}
                style={{
                  background: 'none', border: 'none', cursor: teams.length > 1 ? 'pointer' : 'default',
                  display: 'flex', alignItems: 'center', gap: 3,
                  fontSize: 13, color: teamColor || 'var(--ice)', fontFamily: 'var(--font-body)', fontWeight: 700,
                  padding: '4px 6px', borderRadius: 5,
                  flex: 1, minWidth: 0,
                }}>
                <span style={{
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  flex: 1, minWidth: 0, textAlign: 'left',
                }}>
                  {activeTeam.name}
                </span>
                {teams.length > 1 && <span style={{ fontSize: 9, opacity: 0.65, flexShrink: 0 }}>⇄</span>}
              </button>
              {teams.length > 1 && (
                <button
                  onClick={() => cycleToTeam('next')}
                  aria-label="Next team"
                  style={{
                    ...touchBtn,
                    padding: 6, borderRadius: 6,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    minWidth: 32, minHeight: 32, flexShrink: 0,
                  }}>
                  <ChevronRight size={18} color={teamColor || 'var(--ice)'} strokeWidth={2} />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Notification bell */}
        <button
          onClick={() => { setShowNotifs(p => !p); loadNotifications(); }}
          style={{
            ...touchBtn, padding: 10, borderRadius: 10, position: 'relative',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            minWidth: 48, minHeight: 48,
          }}
        >
          <Bell size={22} color={unreadNotifs > 0 ? teamColor : S.text2} />
          {unreadNotifs > 0 && (
            <span style={{
              position: 'absolute', top: 8, right: 8,
              minWidth: 18, height: 18, padding: '0 4px',
              borderRadius: 9, background: '#ff3b3b',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 800, color: '#fff', lineHeight: 1,
            }}>
              {unreadNotifs > 9 ? '9+' : unreadNotifs}
            </span>
          )}
        </button>
      </div>

      {/* ── Scrollable content ────────────────────────────────── */}
      <div style={{
        flex: 1, overflowY: 'auto', overflowX: 'hidden',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: `calc(${TABBAR_H}px + env(safe-area-inset-bottom, 0px) + var(--keyboard-h, 0px) + 8px)`,
      }}>
        <div style={{ padding: '16px 14px', maxWidth: 640, margin: '0 auto' }}>
          <Outlet context={{ teams, teamsLoaded, activeTeam, setActiveTeam, loadTeams, currentSeasonId, currentSeasonName, setCurrentSeason, seasonLoaded }} />
        </div>
      </div>

      {/* Onboarding overlay — shown once on first login */}
      <OnboardingFlow teams={teams} activeTeam={activeTeam} />

      {/* ── Drawer ─────────────────────────────────────────────── */}
      <CapacitorDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        teams={teams} activeTeam={activeTeam} setActiveTeam={setActiveTeam}
        user={user} onLogout={handleLogout}
        currentSeasonId={currentSeasonId} currentSeasonName={currentSeasonName}
      />

      {/* ── Notifications sheet ───────────────────────────────── */}
      {showNotifs && (
        // Slide down from top (accounts for status bar)
        <>
          <div onClick={() => setShowNotifs(false)} style={{
            position: 'fixed', inset: 0, zIndex: 490, background: 'rgba(0,0,0,0.55)',
          }} />
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0,
            maxHeight: '70dvh', zIndex: 500,
            background: S.surface,
            borderBottom: `1px solid ${S.border}`,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            paddingTop: 'env(safe-area-inset-top, 0px)',
            contain: 'layout style paint',
            transform: 'translateZ(0)',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 16px', borderBottom: `1px solid ${S.border}`,
              position: 'sticky', top: 'env(safe-area-inset-top, 0px)', background: S.surface, zIndex: 1,
            }}>
              <span style={{
                fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 17,
                letterSpacing: '0.05em', textTransform: 'uppercase', color: S.text,
              }}>Notifications</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={async () => {
                  try { await api.put('/notifications/read-all'); setNotifications(p => p.map(n => ({ ...n, read_at: new Date().toISOString() }))); } catch {}
                  setShowNotifs(false);
                }} style={{
                  ...touchBtn, fontSize: 12, color: teamColor, padding: '8px 12px',
                  borderRadius: 8, background: `${teamColor}15`, border: `1px solid ${teamColor}30`,
                  minHeight: 44, fontFamily: 'var(--font-body)', fontWeight: 600,
                }}>All read</button>
                <button onClick={() => setShowNotifs(false)} style={{
                  ...touchBtn, padding: 10, borderRadius: 8, minWidth: 44, minHeight: 44,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <X size={18} color={S.text2} />
                </button>
              </div>
            </div>
            {notifications.length === 0 ? (
              <div style={{ padding: '50px 20px', textAlign: 'center', color: S.text2, fontSize: 15 }}>
                <Bell size={30} style={{ margin: '0 auto 14px', opacity: 0.2, display: 'block' }} />
                All caught up!
              </div>
            ) : notifications.slice(0, 20).map(n => (
              <button key={n.id} onClick={async () => {
                // Capture nav target before the await — state may be stale after an
                // async gap if a WS event arrives mid-flight (same fix as MobileLayout)
                const target = n.game_id
                  ? `/game/${n.game_id}`
                  : n.team_id
                  ? `/team/${n.team_id}/chat`
                  : null;
                try { await api.put(`/notifications/${n.id}/read`); setNotifications(p => p.map(x => x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x)); } catch {}
                setShowNotifs(false);
                if (target) navigate(target);
              }} style={{
                ...touchBtn, width: '100%', textAlign: 'left',
                display: 'flex', alignItems: 'flex-start', gap: 14,
                padding: '15px 16px', borderBottom: `1px solid ${S.border}`,
                background: n.read_at ? 'transparent' : `${teamColor}05`,
                minHeight: 64,
              }}>
                <span style={{ fontSize: 20, flexShrink: 0, marginTop: 2 }}>
                  {iconForNotifType(n.type)}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: S.text, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</span>
                    {!n.read_at && <div style={{ width: 8, height: 8, borderRadius: '50%', background: teamColor, flexShrink: 0 }} />}
                  </div>
                  {n.body && <p style={{ fontSize: 13, color: S.text2, margin: '4px 0 0', lineHeight: 1.45 }}>{n.body}</p>}
                  <p style={{ fontSize: 11, color: S.text2, opacity: 0.5, marginTop: 4 }}>
                    {new Date(n.created_at).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {/* ── Native tab bar ─────────────────────────────────────── */}
      {/* Apr 2026 — 4-tab layout. "More" opens the side drawer where
          Profile, Notifications, Players, Seasons, Stats, Finance,
          Notification Log, Team Settings, Logout, etc. all live. */}
      <NativeTabBar
        activeTeam={activeTeam}
        unreadCount={unreadCount}
        unreadNotifs={unreadNotifs}
        onMoreClick={() => { haptics.impact('light'); setDrawerOpen(true); }}
        teamColor={teamColor}
      />
    </div>
  );
}
